//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { FileUploaderAssetFailure } from '@campaigns/components/shared/file-uploader/FileUploaderTypes';
import { uploadFiles } from '@campaigns/components/shared/file-uploader/UploadFiles';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { BodyBoldText, BodySecondaryText } from '@components/atoms/text/Text';
import { TrackingId } from '@models/TrackingId';
import { contentDrawerActions } from '@redux/content-drawer/contentDrawerActions';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import { SupportedMimeTypes } from '@utils/mimeTypes';
import { withTracking } from '@utils/withTracking';

interface BaseFileUploaderProps {
  inputId?: string;
  isDisabled?: boolean;
  maxTotalSize?: number;
  actionText: string;
  supportedFileTypes: SupportedMimeTypes[];
  title: React.ReactNode;
  trackingId?: TrackingId;
  onFilesSelected: (
    files: File[],
    failureReasons: FileUploaderAssetFailure,
  ) => Promise<void> | void;
  existingAssetUrls: string[];
}
interface FileUploaderMultipleFilesProps extends BaseFileUploaderProps {
  shouldAcceptMultipleFiles: true;
  maxNumberOfFiles: number; // `maxNumberOfFiles` is required
}
interface FileUploaderSingleFileProps extends BaseFileUploaderProps {
  shouldAcceptMultipleFiles: false;
  maxNumberOfFiles?: never; // `maxNumberOfFiles` is not valid
}

export const DEFAULT_MAX_TOTAL_SIZE_MB = 25 * 1024 * 1024;

export const FileUploader = ({
  actionText,
  existingAssetUrls,
  inputId,
  isDisabled = false,
  maxNumberOfFiles,
  maxTotalSize = DEFAULT_MAX_TOTAL_SIZE_MB,
  onFilesSelected,
  shouldAcceptMultipleFiles = false,
  supportedFileTypes,
  title,
  trackingId,
}: FileUploaderMultipleFilesProps | FileUploaderSingleFileProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragEnter, setDragEnter] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      contentDrawerActions.setOverrideSettings({
        disableImageUploader: true,
        disableContentDrawer: true,
      }),
    );

    return () => {
      dispatch(contentDrawerActions.unsetOverrideSettings());
    };
  }, [dispatch]);

  const { isLoading, mutate: handleFileSelect } = useMutation({
    mutationFn: async (selectedFileList: FileList | null) => {
      const result = await uploadFiles({
        files: selectedFileList,
        existingAssetUrls,
        shouldAcceptMultipleFiles,
        maxNumberOfFiles,
        maxTotalSize,
        supportedFileTypes,
      });

      if (
        result.files.length > 0 ||
        Object.keys(result.failureReasons).length > 0
      ) {
        await onFilesSelected(result.files, result.failureReasons);
      }

      // Clear the file input value to allow the same file to be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
  });

  return (
    <UploadContainer
      isDisabled={isDisabled}
      isDragEnter={isDragEnter}
      onDragEnter={(event) => {
        // Prevents the default browser action like opening the dragged file.
        event.preventDefault();
        setDragEnter(true);
      }}
      onDragLeave={(event) => {
        // Prevents the default browser action like opening the dragged file.
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const { clientX, clientY } = event;

        // make sure the dragged file is with in the border of the component or not
        if (
          !clientX ||
          !clientY ||
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        ) {
          setDragEnter(false);
        }
      }}
      onDragOver={(event) => {
        // Prevents the default browser action like opening the dragged-over file.
        event.preventDefault();
      }}
      onDrop={(event) => {
        // Prevents the default browser action like opening the dropped file. Want to process it instead.
        event.preventDefault();
        setDragEnter(false);
        void handleFileSelect(event.dataTransfer.files);
      }}
      trackingId={trackingId}
    >
      <UploadIcon name={isLoading ? 'spinner' : 'upload'} />
      <UploadContainerHeaders>
        {typeof title === 'string' ? (
          <BodyBoldText>{title}</BodyBoldText>
        ) : (
          title
        )}
        <ActionTextContainer>
          <BodySecondaryText>or</BodySecondaryText>
          <PrimaryLinkButton
            isDisabled={isDisabled || isLoading}
            onClick={() => inputRef.current?.click()}
            trackingId={trackingId}
            type='button'
          >
            {actionText}
          </PrimaryLinkButton>
        </ActionTextContainer>
      </UploadContainerHeaders>
      <InvisibleInput
        accept={supportedFileTypes.join(', ')}
        disabled={isDisabled}
        title='file upload'
        {...(isDefined(inputId) && { id: inputId })}
        ref={inputRef}
        multiple={shouldAcceptMultipleFiles}
        onChange={(e) => void handleFileSelect(e.target.files)}
        tabIndex={-1}
        type='file'
      />
    </UploadContainer>
  );
};

const UploadContainer = withTracking(
  styled.div<{
    isDisabled: boolean;
    isDragEnter: boolean;
  }>(
    ({ isDisabled, isDragEnter, theme }) => css`
      display: flex;
      align-items: center;
      justify-content: flex-start;
      border: ${theme.borderWidths[1]} dashed
        ${theme.colors.aliases.bodySecondary};
      border-radius: ${theme.borderRadii[2]};
      background-color: ${theme.colors.white};
      gap: ${theme.spacings[3]};
      padding: ${theme.spacings[3]};
      cursor: pointer;
      ${isDragEnter &&
      `border: ${theme.borderWidths[1]} solid ${theme.colors.aliases.primary};`}
      ${isDisabled &&
      `
        pointer-events: none;
        opacity: 0.5;
        cursor: default;
  `}
    `,
  ),
);

const ActionTextContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${theme.spacings[1]};
  `,
);

const UploadContainerHeaders = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const UploadIcon = styled(Icon)(
  ({ theme }) => `
  color: ${theme.colors.black};
  width: ${theme.spacings[3]};
  height: ${theme.spacings[3]};
`,
);

const InvisibleInput = styled.input(
  () => css`
    ${Mixins.VisuallyHidden}
  `,
);
